import { useEffect, useId, useRef, useState, KeyboardEvent } from "react";

/**
 * Custom select following the WAI-ARIA "select-only combobox" pattern.
 * - Button (role=combobox) + listbox (role=listbox) of options
 * - Options render in the DOM, so long labels wrap instead of being clipped
 * - Keyboard: ArrowUp/Down, Home/End, Enter/Space, Esc, Tab, type-ahead
 */

export interface SelectOption {
  value: string;
  label: string;
}

interface AccessibleSelectProps {
  label?: string;
  labelledBy?: string;
  placeholder?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export default function AccessibleSelect({
  label,
  labelledBy,
  placeholder = "",
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  className = "",
  id,
}: AccessibleSelectProps) {
  const uid = useId();
  const baseId = id ?? uid;
  const listId = `${baseId}-listbox`;
  const optionId = (i: number) => `${baseId}-option-${i}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef({ text: "", timer: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const openList = () => {
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const closeList = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  const choose = (index: number) => {
    onChange(options[index].value);
    closeList();
  };

  // Close when clicking/tapping outside
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // Keep the active option visible inside the scrollable list
  useEffect(() => {
    if (!open || active < 0) return;
    (listRef.current?.children[active] as HTMLElement | undefined)?.scrollIntoView({
      block: "nearest",
    });
  }, [open, active]);

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) openList();
        else setActive((i) => Math.min(i + 1, last));
        return;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openList();
        else setActive((i) => Math.max(i - 1, 0));
        return;
      case "Home":
        if (open) {
          e.preventDefault();
          setActive(0);
        }
        return;
      case "End":
        if (open) {
          e.preventDefault();
          setActive(last);
        }
        return;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) openList();
        else if (active >= 0) choose(active);
        return;
      case "Escape":
        if (open) {
          // stop the event so a parent Bootstrap Modal doesn't close too
          e.preventDefault();
          e.stopPropagation();
          closeList();
        }
        return;
      case "Tab":
        if (open) setOpen(false);
        return;
    }

    // Type-ahead: jump to the option starting with the typed characters
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const t = typeahead.current;
      window.clearTimeout(t.timer);
      t.text += e.key.toLowerCase();
      t.timer = window.setTimeout(() => (t.text = ""), 600);

      const match = options.findIndex((o) => o.label.toLowerCase().startsWith(t.text));
      if (match >= 0) {
        if (!open) setOpen(true);
        setActive(match);
      }
    }
  };

  return (
    <div className={`aioa-select ${className}`.trim()} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        id={baseId}
        role="combobox"
        disabled={disabled}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-required={required || undefined}
        className={`form-select aioa-select__button${selected ? "" : " aioa-select__button--placeholder"}`}
        onClick={() => (open ? closeList(false) : openList())}
        onKeyDown={handleKeyDown}
      >
        {selected ? selected.label : placeholder}
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          className="aioa-select__list"
        >
          {options.map((opt, i) => (
            <li
              key={opt.value}
              id={optionId(i)}
              role="option"
              aria-selected={opt.value === value}
              className={
                "aioa-select__option" +
                (i === active ? " is-active" : "") +
                (opt.value === value ? " is-selected" : "")
              }
              // mousedown (not click) so focus never leaves the combobox button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(i)}
              onMouseMove={() => active !== i && setActive(i)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {/* Keeps native form validation working for `required` */}
      {required && (
        <input
          className="aioa-select__validator"
          tabIndex={-1}
          aria-hidden="true"
          required
          value={value}
          onChange={() => {}}
          onFocus={() => buttonRef.current?.focus()}
        />
      )}
    </div>
  );
}